import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {HttpErrorResponse} from '@angular/common/http';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize, map, startWith, switchMap, takeUntil} from 'rxjs/operators';
import {FolderService} from '../../../services/folder/folder.service';
import {NotificationService} from '../../../services/common/notification.service';
import {FOLDER_ACCESS_LABELS, FolderItem, SimpleFolder, folderUserName} from '../../../dtos/folder/folder';
import {folderErrorMessage, MAX_FOLDER_ITEMS} from '../../../shared/folder/folder-utils';

export interface AddToFolderDialogData {
  items: FolderItem[];
  /** folder that is not offered as target, e.g. the one the items are copied from */
  excludeFolderId?: number;
}

const NEW_FOLDER = 'new';

/**
 * adds the given entries to a Sammelmappe the user owns or may edit, or to a new one.
 * Closes with `true` when the entries were added.
 */
@Component({
  selector: 'app-add-to-folder-dialog',
  templateUrl: './add-to-folder-dialog.component.html',
  styleUrls: ['./add-to-folder-dialog.component.scss'],
  standalone: false
})
export class AddToFolderDialogComponent implements OnInit, OnDestroy {

  readonly maxItems = MAX_FOLDER_ITEMS;

  readonly newFolder = NEW_FOLDER;
  readonly accessLabels = FOLDER_ACCESS_LABELS;
  readonly folderUserName = folderUserName;

  searchCtrl = new FormControl('');
  nameCtrl = new FormControl('', [Validators.required, Validators.maxLength(256)]);
  folders: SimpleFolder[] = [];
  selected: number | typeof NEW_FOLDER = null;
  loading = true;
  saving = false;

  private destroy$ = new Subject<void>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: AddToFolderDialogData,
              private dialogRef: MatDialogRef<AddToFolderDialogComponent, boolean>,
              private folderService: FolderService,
              private notificationService: NotificationService) {
  }

  ngOnInit(): void {
    this.searchCtrl.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      map(name => name?.trim() ?? ''),
      distinctUntilChanged(),
      switchMap(name => {
        this.loading = true;
        return this.folderService.getFolders({scope: 'ALL', name, size: 200, sort: 'updatedAt', order: 'desc'});
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: page => {
        this.loading = false;
        // only lists the user may change are valid targets
        this.folders = page.content.filter(f =>
          (f.access === 'OWNER' || f.access === 'EDIT') && f.id !== this.data.excludeFolderId);
        if (this.selected !== NEW_FOLDER && !this.folders.some(f => f.id === this.selected)) {
          this.selected = null;
        }
      },
      error: error => {
        this.loading = false;
        this.notificationService.openSnackBar(folderErrorMessage(error));
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get itemCount(): number {
    return this.data.items.length;
  }

  canSubmit(): boolean {
    if (this.saving || this.itemCount === 0) {
      return false;
    }
    if (this.selected === NEW_FOLDER) {
      return this.nameCtrl.valid && !!this.nameCtrl.value.trim();
    }
    return this.selected !== null;
  }

  submit(): void {
    if (!this.canSubmit()) {
      this.nameCtrl.markAsTouched();
      return;
    }
    this.saving = true;
    if (this.selected === NEW_FOLDER) {
      const name = this.nameCtrl.value.trim();
      this.folderService.createFolder({name, items: this.data.items})
        .pipe(finalize(() => this.saving = false))
        .subscribe({
          next: folder => {
            this.notificationService.openSnackBar('Sammelmappe „' + folder.name + '“ erstellt ('
              + this.entryText(folder.itemCount) + ').');
            this.dialogRef.close(true);
          },
          error: error => this.handleError(error)
        });
    } else {
      const target = this.folders.find(f => f.id === this.selected);
      this.folderService.addItems(target.id, this.data.items)
        .pipe(finalize(() => this.saving = false))
        .subscribe({
          next: folder => {
            const added = folder.itemCount - target.itemCount;
            const text = added > 0
              ? this.entryText(added) + ' zu „' + folder.name + '“ hinzugefügt.'
              : 'Alle Einträge sind bereits in „' + folder.name + '“ enthalten.';
            this.notificationService.openSnackBar(text);
            this.dialogRef.close(true);
          },
          error: error => this.handleError(error)
        });
    }
  }

  entryText(count: number): string {
    return count + (count === 1 ? ' Eintrag' : ' Einträge');
  }

  private handleError(error: HttpErrorResponse): void {
    this.notificationService.openSnackBar(folderErrorMessage(error, {
      400: 'Die Einträge konnten nicht hinzugefügt werden.',
      403: 'Sie dürfen diese Sammelmappe nicht bearbeiten.',
      404: 'Mindestens ein Eintrag existiert nicht mehr.'
    }));
  }
}
